/**
 * Was ein MCP-Zugang darf — Werkzeug für Werkzeug.
 *
 * Ein Token liegt in einer Konfigurationsdatei auf einer fremden Platte. Es
 * bekommt deshalb nicht die vollen Rechte seiner Person, sondern eine
 * ausdrückliche Liste erlaubter Werkzeuge. Was nicht darin steht, wird weder
 * ausgeführt noch überhaupt angeboten.
 *
 * **Die Einteilung steht hier von Hand, nicht als Regel über den Namen.**
 * Naheliegend wäre gewesen, alles mit der Endung `_lesen` als lesend zu
 * zählen. Das ist falsch: `cubic_marke_aufloesen` löst eine Textstelle auf und
 * verlangt `erwarteteVersion` — es schreibt. `cubic_suche` und
 * `cubic_verweise_aufloesen` tragen die Endung nicht und lesen trotzdem nur.
 * Eine Namensregel hätte in beiden Richtungen danebengegriffen, und bei einer
 * Rechteprüfung ist das kein Schönheitsfehler.
 *
 * Der Bereich ist reine Bedienbarkeit: Mehr als hundert einzelne Kästchen
 * bedient niemand, zwölf Gruppen mit „lesen" und „schreiben" schon.
 */
export const BEREICHSNAMEN = {
    aufgaben: "Aufgaben und Meilensteine",
    seiten: "Seiten, Marken und Vorlagen",
    projekte: "Projekte und Personen",
    gitlab: "GitLab",
    durchsicht: "Durchsicht und Befunde",
    klaerungen: "Klärungen",
    kommentare: "Kommentare",
    metarr: "Metarr",
    regeln: "Regeln",
    auto_qa: "Auto Q&A und Anlässe",
    content: "Content und Serien",
    verweise: "Verweise und Suche",
    persoenlich: "Persönliches",
};
/**
 * Jedes MCP-Werkzeug mit Bereich und Art.
 *
 * Kommt ein Werkzeug dazu und fehlt hier, schlägt der Test in
 * `apps/server/src/mcp/werkzeuge.test.ts` fehl. Das ist Absicht: Ein Werkzeug
 * ohne Eintrag wäre sonst still von jedem Zugang ausgeschlossen — oder,
 * schlimmer, versehentlich in jedem enthalten.
 */
export const WERKZEUGVERZEICHNIS = {
    /* Aufgaben und Meilensteine */
    cubic_aufgaben_lesen: { bereich: "aufgaben", art: "lesen" },
    cubic_aufgabe_lesen: { bereich: "aufgaben", art: "lesen" },
    cubic_akzeptanzkriterien_lesen: { bereich: "aufgaben", art: "lesen" },
    cubic_meilensteine_lesen: { bereich: "aufgaben", art: "lesen" },
    cubic_meilenstein_lesen: { bereich: "aufgaben", art: "lesen" },
    cubic_arbeitspakete_lesen: { bereich: "aufgaben", art: "lesen" },
    cubic_arbeitspaket_lesen: { bereich: "aufgaben", art: "lesen" },
    cubic_planobjekte_suchen: { bereich: "aufgaben", art: "lesen" },
    cubic_abhaengigkeiten_lesen: { bereich: "aufgaben", art: "lesen" },
    cubic_labels_lesen: { bereich: "aufgaben", art: "lesen" },
    cubic_beobachter_lesen: { bereich: "aufgaben", art: "lesen" },
    cubic_aufgabe_anlegen: {
        bereich: "aufgaben",
        art: "schreiben",
        additiv: true,
    },
    cubic_aufgabe_aendern: { bereich: "aufgaben", art: "schreiben" },
    cubic_aufgabe_archivieren: {
        bereich: "aufgaben",
        art: "schreiben",
        destruktiv: true,
    },
    cubic_aufgabe_wiederherstellen: { bereich: "aufgaben", art: "schreiben" },
    cubic_aufgabe_status: { bereich: "aufgaben", art: "schreiben" },
    cubic_aufgabe_zuweisen: { bereich: "aufgaben", art: "schreiben" },
    cubic_aufgabe_label_setzen: { bereich: "aufgaben", art: "schreiben" },
    cubic_akzeptanzkriterium_anlegen: {
        bereich: "aufgaben",
        art: "schreiben",
        additiv: true,
    },
    cubic_akzeptanzkriterium_aendern: { bereich: "aufgaben", art: "schreiben" },
    cubic_akzeptanzkriterium_loeschen: {
        bereich: "aufgaben",
        art: "schreiben",
        destruktiv: true,
    },
    cubic_meilenstein_anlegen: {
        bereich: "aufgaben",
        art: "schreiben",
        additiv: true,
    },
    cubic_meilenstein_aendern: { bereich: "aufgaben", art: "schreiben" },
    cubic_meilenstein_archivieren: {
        bereich: "aufgaben",
        art: "schreiben",
        destruktiv: true,
    },
    cubic_meilenstein_wiederherstellen: {
        bereich: "aufgaben",
        art: "schreiben",
    },
    cubic_arbeitspaket_anlegen: {
        bereich: "aufgaben",
        art: "schreiben",
        additiv: true,
    },
    cubic_arbeitspaket_aendern: { bereich: "aufgaben", art: "schreiben" },
    cubic_arbeitspaket_archivieren: {
        bereich: "aufgaben",
        art: "schreiben",
        destruktiv: true,
    },
    cubic_arbeitspaket_wiederherstellen: {
        bereich: "aufgaben",
        art: "schreiben",
    },
    cubic_abhaengigkeit_setzen: { bereich: "aufgaben", art: "schreiben" },
    cubic_abhaengigkeit_entfernen: {
        bereich: "aufgaben",
        art: "schreiben",
        destruktiv: true,
    },
    cubic_beobachter_setzen: { bereich: "aufgaben", art: "schreiben" },
    /* Seiten, Marken und Vorlagen */
    cubic_seiten_lesen: { bereich: "seiten", art: "lesen" },
    cubic_seite_lesen: { bereich: "seiten", art: "lesen" },
    cubic_seitenbaum_lesen: { bereich: "seiten", art: "lesen" },
    cubic_seitenfassung_lesen: { bereich: "seiten", art: "lesen" },
    cubic_seitenfassungen_lesen: { bereich: "seiten", art: "lesen" },
    cubic_marken_lesen: { bereich: "seiten", art: "lesen" },
    cubic_vorlagen_lesen: { bereich: "seiten", art: "lesen" },
    cubic_seite_anlegen: { bereich: "seiten", art: "schreiben", additiv: true },
    cubic_seite_schreiben: { bereich: "seiten", art: "schreiben" },
    cubic_seite_archivieren: {
        bereich: "seiten",
        art: "schreiben",
        destruktiv: true,
    },
    cubic_seite_wiederherstellen: { bereich: "seiten", art: "schreiben" },
    cubic_seite_verschieben: { bereich: "seiten", art: "schreiben" },
    cubic_seite_aus_vorlage: {
        bereich: "seiten",
        art: "schreiben",
        additiv: true,
    },
    cubic_seitenfassung_erstellen: {
        bereich: "seiten",
        art: "schreiben",
        additiv: true,
    },
    cubic_seitenfassung_wiederherstellen: {
        bereich: "seiten",
        art: "schreiben",
    },
    cubic_marke_anlegen: { bereich: "seiten", art: "schreiben", additiv: true },
    /* Löst eine Textstelle auf und verlangt erwarteteVersion — das schreibt. */
    cubic_marke_aufloesen: {
        bereich: "seiten",
        art: "schreiben",
        destruktiv: true,
    },
    /* Projekte und Personen */
    cubic_projekte_lesen: { bereich: "projekte", art: "lesen" },
    cubic_projekt_lesen: { bereich: "projekte", art: "lesen" },
    cubic_projektverzeichnis_lesen: { bereich: "projekte", art: "lesen" },
    cubic_personen_lesen: { bereich: "projekte", art: "lesen" },
    cubic_projekt_anlegen: {
        bereich: "projekte",
        art: "schreiben",
        additiv: true,
    },
    cubic_projekt_aendern: { bereich: "projekte", art: "schreiben" },
    cubic_projekt_archivieren: {
        bereich: "projekte",
        art: "schreiben",
        destruktiv: true,
    },
    cubic_projekt_wiederherstellen: { bereich: "projekte", art: "schreiben" },
    /* GitLab */
    cubic_mr_lesen: { bereich: "gitlab", art: "lesen" },
    cubic_qualitaets_gate_lesen: { bereich: "gitlab", art: "lesen" },
    cubic_unzugeordnete_mr_lesen: { bereich: "gitlab", art: "lesen" },
    cubic_pipelines_lesen: { bereich: "gitlab", art: "lesen" },
    cubic_commits_lesen: { bereich: "gitlab", art: "lesen" },
    cubic_auslieferungen_lesen: { bereich: "gitlab", art: "lesen" },
    cubic_mr_verknuepfen: { bereich: "gitlab", art: "schreiben" },
    cubic_mr_freigeben: { bereich: "gitlab", art: "schreiben" },
    cubic_mr_mergen: {
        bereich: "gitlab",
        art: "schreiben",
        destruktiv: true,
    },
    /* Durchsicht und Befunde */
    cubic_befunde_lesen: { bereich: "durchsicht", art: "lesen" },
    cubic_laeufe_lesen: { bereich: "durchsicht", art: "lesen" },
    cubic_rezepte_lesen: { bereich: "durchsicht", art: "lesen" },
    cubic_rezept_lesen: { bereich: "durchsicht", art: "lesen" },
    cubic_hausregeln_lesen: { bereich: "durchsicht", art: "lesen" },
    cubic_durchsicht_anfordern: { bereich: "durchsicht", art: "schreiben" },
    cubic_befund_kommentieren: { bereich: "durchsicht", art: "schreiben" },
    cubic_rezept_anlegen: {
        bereich: "durchsicht",
        art: "schreiben",
        additiv: true,
    },
    cubic_rezept_fassung_anlegen: {
        bereich: "durchsicht",
        art: "schreiben",
        additiv: true,
    },
    cubic_rezept_aendern: { bereich: "durchsicht", art: "schreiben" },
    cubic_rezept_archivieren: {
        bereich: "durchsicht",
        art: "schreiben",
        destruktiv: true,
    },
    cubic_rezept_wiederherstellen: {
        bereich: "durchsicht",
        art: "schreiben",
    },
    cubic_hausregel_anlegen: {
        bereich: "durchsicht",
        art: "schreiben",
        additiv: true,
    },
    /* Klärungen */
    cubic_klaerungen_lesen: { bereich: "klaerungen", art: "lesen" },
    cubic_klaerung_anlegen: {
        bereich: "klaerungen",
        art: "schreiben",
        additiv: true,
    },
    cubic_klaerung_beantworten: { bereich: "klaerungen", art: "schreiben" },
    cubic_klaerung_klaeren: { bereich: "klaerungen", art: "schreiben" },
    /* Kommentare */
    cubic_kommentare_lesen: { bereich: "kommentare", art: "lesen" },
    cubic_kommentar_schreiben: { bereich: "kommentare", art: "schreiben" },
    /* Metarr */
    cubic_metarr_codearbeitspaket_optionen: { bereich: "metarr", art: "lesen" },
    cubic_metarr_codearbeitspakete_lesen: { bereich: "metarr", art: "lesen" },
    cubic_metarr_codearbeitspaket_lesen: { bereich: "metarr", art: "lesen" },
    cubic_metarr_codearbeitspaket_starten: {
        bereich: "metarr",
        art: "schreiben",
        additiv: true,
    },
    cubic_metarr_codearbeitspaket_abbrechen: {
        bereich: "metarr",
        art: "schreiben",
        destruktiv: true,
    },
    cubic_entwuerfe_lesen: { bereich: "metarr", art: "lesen" },
    cubic_hausmeister_laeufe_lesen: { bereich: "metarr", art: "lesen" },
    cubic_entwurf_anlegen: {
        bereich: "metarr",
        art: "schreiben",
        additiv: true,
    },
    cubic_hausmeister_starten: { bereich: "metarr", art: "schreiben" },
    /* Regeln */
    cubic_regeln_lesen: { bereich: "regeln", art: "lesen" },
    cubic_regel_lesen: { bereich: "regeln", art: "lesen" },
    cubic_regel_laeufe_lesen: { bereich: "regeln", art: "lesen" },
    cubic_regel_anlegen: {
        bereich: "regeln",
        art: "schreiben",
        additiv: true,
    },
    cubic_regel_aendern: { bereich: "regeln", art: "schreiben" },
    cubic_regel_pausieren: { bereich: "regeln", art: "schreiben" },
    cubic_regel_aktivieren: { bereich: "regeln", art: "schreiben" },
    cubic_regel_loeschen: {
        bereich: "regeln",
        art: "schreiben",
        destruktiv: true,
    },
    /* Auto Q&A und normalisierte Anlässe */
    cubic_auto_qa_katalog_lesen: { bereich: "auto_qa", art: "lesen" },
    cubic_auto_qa_automationen_lesen: { bereich: "auto_qa", art: "lesen" },
    cubic_auto_qa_ausloesungen_lesen: { bereich: "auto_qa", art: "lesen" },
    cubic_auto_qa_manuell_ausloesen: {
        bereich: "auto_qa",
        art: "schreiben",
        additiv: true,
    },
    /* Content und Serien */
    cubic_serien_lesen: { bereich: "content", art: "lesen" },
    cubic_serie_anlegen: { bereich: "content", art: "schreiben", additiv: true },
    /* Verweise und Suche — beide lesen, trotz fehlender Endung. */
    cubic_suche: { bereich: "verweise", art: "lesen" },
    cubic_beziehungen_lesen: { bereich: "verweise", art: "lesen" },
    cubic_verweise_aufloesen: { bereich: "verweise", art: "lesen" },
    cubic_beziehung_setzen: { bereich: "verweise", art: "schreiben" },
    cubic_verknuepfung_entfernen: {
        bereich: "verweise",
        art: "schreiben",
        destruktiv: true,
    },
    /* Persönliches */
    cubic_sichten_lesen: { bereich: "persoenlich", art: "lesen" },
    cubic_einstellungen_lesen: { bereich: "persoenlich", art: "lesen" },
    cubic_kapazitaet_lesen: { bereich: "persoenlich", art: "lesen" },
    cubic_benachrichtigungen_lesen: { bereich: "persoenlich", art: "lesen" },
    cubic_benachrichtigung_gelesen: {
        bereich: "persoenlich",
        art: "schreiben",
    },
    cubic_verlauf_lesen: { bereich: "persoenlich", art: "lesen" },
    cubic_verbrauch_lesen: { bereich: "persoenlich", art: "lesen" },
    cubic_sicht_anlegen: {
        bereich: "persoenlich",
        art: "schreiben",
        additiv: true,
    },
    cubic_einstellung_setzen: { bereich: "persoenlich", art: "schreiben" },
};
/** Alle Werkzeugnamen, die überhaupt vergeben werden können. */
export function alleWerkzeuge() {
    return Object.keys(WERKZEUGVERZEICHNIS).sort();
}
/** Die Werkzeuge einer Art — Grundlage der Vorgabesätze. */
export function werkzeugeDerArt(art) {
    return alleWerkzeuge().filter((name) => WERKZEUGVERZEICHNIS[name]?.art === art);
}
/** Die Werkzeuge eines Bereichs, wahlweise nur einer Art. */
export function werkzeugeDesBereichs(bereich, art) {
    return alleWerkzeuge().filter((name) => {
        const eintrag = WERKZEUGVERZEICHNIS[name];
        if (!eintrag || eintrag.bereich !== bereich)
            return false;
        return art === undefined || eintrag.art === art;
    });
}
/**
 * Die zwei Sätze, die fast jeder nimmt.
 *
 * `voll` ist nicht „alles, was die Person darf" — es ist „alle Werkzeuge".
 * Was die Person tatsächlich sehen und ändern darf, entscheidet weiterhin ihre
 * Sicht beim Aufruf. Der Zugang kann nur wegnehmen.
 */
export function vorgabesatz(satz) {
    return satz === "voll" ? alleWerkzeuge() : werkzeugeDerArt("lesen");
}
/**
 * Darf dieser Zugang dieses Werkzeug?
 *
 * Unbekannte Namen werden abgewiesen, auch wenn sie in der Liste stehen: Ein
 * Zugang, der auf ein entferntes Werkzeug ausgestellt wurde, soll nicht
 * plötzlich ein neues gleichen Namens erwischen.
 */
export function darfWerkzeug(erlaubte, name) {
    if (!(name in WERKZEUGVERZEICHNIS))
        return false;
    return erlaubte.includes(name);
}
/**
 * Enthält die Auswahl irgendetwas Schreibendes?
 *
 * Wenn nicht, bekommt die Sicht zusätzlich `nurLesend`. Zwei Schlösser vor
 * derselben Tür: Die Werkzeugliste sperrt den Aufruf, `nurLesend` sperrt das
 * Schreiben auch dann noch, wenn ein Werkzeug hier falsch eingeordnet wäre.
 */
export function schreibtIrgendwas(erlaubte) {
    return erlaubte.some((name) => WERKZEUGVERZEICHNIS[name]?.art === "schreiben");
}
/** Kurzfassung für die Liste: „12 lesend, 3 schreibend". */
export function rechteZusammenfassung(erlaubte) {
    let lesen = 0;
    let schreiben = 0;
    for (const name of erlaubte) {
        const eintrag = WERKZEUGVERZEICHNIS[name];
        if (!eintrag)
            continue;
        if (eintrag.art === "lesen")
            lesen += 1;
        else
            schreiben += 1;
    }
    return { lesen, schreiben };
}
//# sourceMappingURL=mcp-rechte.js.map